import { FC, useEffect, useRef, useState } from "react";
import { AccountingMigrationResponse } from "@common/schemas/accounting-integration.schema";
import { MigrationObjectType } from "@common/types/domains/accounting/accountingMigration";
import { FullMigrationOptions } from "@components/accountingMigration/historicalData";
import historicalDataStyles from "@components/accountingMigration/historicalData/HistoricalDataFields.module.scss";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { HStack } from "@design-system/HStack/HStack";
import {
    StandardModal,
    StandardModalBody,
    StandardModalFooter,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import {
    initialRefreshSelection,
    lockedRefreshObjectTypes,
    refreshPrerequisiteReason,
    toggleRefreshObjectType,
} from "./refreshObjectTypeSelection";

interface Props {
    show: boolean;
    accountingMigration: AccountingMigrationResponse;
    isSubmitting: boolean;
    onClose: () => void;
    onConfirm: (objectTypes: MigrationObjectType[]) => void;
}

function buildLockReasonByObjectType({
    selection,
    lockedObjectTypes,
    accountingMigration,
}: {
    selection: ReadonlySet<MigrationObjectType>;
    lockedObjectTypes: ReadonlySet<MigrationObjectType>;
    accountingMigration: AccountingMigrationResponse;
}): ReadonlyMap<MigrationObjectType, string> {
    const lockReasonByObjectType = new Map<MigrationObjectType, string>();
    for (const objectType of lockedObjectTypes) {
        const reason = refreshPrerequisiteReason({
            selection,
            objectType,
            migration: accountingMigration,
        });
        if (reason) {
            lockReasonByObjectType.set(objectType, reason);
        }
    }
    return lockReasonByObjectType;
}

export const RefreshMigrationModal: FC<Props> = ({
    show,
    accountingMigration,
    isSubmitting,
    onClose,
    onConfirm,
}) => {
    const [selection, setSelection] = useState<
        ReadonlySet<MigrationObjectType>
    >(() => initialRefreshSelection(accountingMigration));
    const wasShownRef = useRef(false);

    useEffect(() => {
        if (!show) {
            wasShownRef.current = false;
            return;
        }

        if (!wasShownRef.current) {
            setSelection(initialRefreshSelection(accountingMigration));
            wasShownRef.current = true;
        }
    }, [show, accountingMigration]);

    const toggle = (objectType: MigrationObjectType) => () => {
        setSelection((current) =>
            toggleRefreshObjectType({
                selection: current,
                objectType,
                migration: accountingMigration,
            }),
        );
    };

    const lockedObjectTypes = lockedRefreshObjectTypes({
        selection,
        migration: accountingMigration,
    });
    const lockReasonByObjectType = buildLockReasonByObjectType({
        selection,
        lockedObjectTypes,
        accountingMigration,
    });

    const isGeneralLedgerTicked = selection.has(
        MigrationObjectType.GENERAL_LEDGER,
    );

    const handleHide = () => {
        if (isSubmitting) {
            return;
        }
        onClose();
    };

    return (
        <StandardModal show={show} onHide={handleHide}>
            <StandardModalHeader>
                Refresh accounting migration
            </StandardModalHeader>
            <StandardModalBody>
                <FullMigrationOptions
                    syncGeneralLedger={isGeneralLedgerTicked}
                    syncCounterparties={selection.has(
                        MigrationObjectType.COUNTERPARTIES,
                    )}
                    syncAttachments={selection.has(
                        MigrationObjectType.ATTACHMENTS,
                    )}
                    syncClasses={selection.has(MigrationObjectType.CLASSES)}
                    syncCatalogItems={selection.has(
                        MigrationObjectType.CATALOG_ITEMS,
                    )}
                    syncInvoices={selection.has(MigrationObjectType.INVOICES)}
                    syncBills={selection.has(MigrationObjectType.BILLS)}
                    onToggleGeneralLedger={toggle(
                        MigrationObjectType.GENERAL_LEDGER,
                    )}
                    onToggleCounterparties={toggle(
                        MigrationObjectType.COUNTERPARTIES,
                    )}
                    onToggleAttachments={toggle(
                        MigrationObjectType.ATTACHMENTS,
                    )}
                    onToggleClasses={toggle(MigrationObjectType.CLASSES)}
                    onToggleCatalogItems={toggle(
                        MigrationObjectType.CATALOG_ITEMS,
                    )}
                    onToggleInvoices={toggle(MigrationObjectType.INVOICES)}
                    onToggleBills={toggle(MigrationObjectType.BILLS)}
                    lockedObjectTypes={lockedObjectTypes}
                    lockReasonByObjectType={lockReasonByObjectType}
                    cascadeIncludesGeneralLedger={false}
                    cascadeIncludesClasses={
                        accountingMigration.syncClasses === true
                    }
                />
                <div className={historicalDataStyles.migrationCheckboxNote}>
                    Refresh only adds records that are not in Kick yet. It never
                    changes or removes records already in Kick.
                </div>
                {isGeneralLedgerTicked && (
                    <div className={historicalDataStyles.migrationCheckboxNote}>
                        Refreshing the general ledger replaces the migrated
                        journal entries. Any edits you made to them are
                        discarded.
                    </div>
                )}
            </StandardModalBody>
            <StandardModalFooter>
                <HStack gap={8} justify="end">
                    <Button
                        variant="secondary"
                        size="md"
                        disabled={isSubmitting}
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                    <ButtonWithLoader
                        variant="destructive"
                        size="md"
                        disabled={selection.size === 0}
                        loading={isSubmitting}
                        onClick={() => onConfirm(Array.from(selection))}
                    >
                        Refresh migration
                    </ButtonWithLoader>
                </HStack>
            </StandardModalFooter>
        </StandardModal>
    );
};
